import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import "./settings.css";

// components imports
import Loader from "../../../../../Components/CircularLoader/CircularLoader";
import WarehouseTable from "../../../../../Components/BoltTable/BoltTable";
import Tooltip from "../../../../../Components/Tooltip/Tooltip";
import AddWarehouseUser from "./Components/ManageWarehouse/AddWarehouseUser/AddWarehouseUser";
import Confirm from "../../../../../Components/Popups/Confirm";
import SideBar from "./Components/SideBar/SideBar";
import { getEffectiveOwnerId } from "../../../../../helpers/helpers";
import BoltButton from "../../../../../Components/UI/Buttons/BoltButton";
import StatusIcons from "../../../../../Components/StatusIcons/StatusIcons";

// apis imports
import {
  getWareHouseList,
  sendWareHouseRequest,
  getUserWarehouses,
  deleteWareHouseRequest,
} from "../../../../../services/ShipperServices/ShipperSettings/shippersettings.services";

// library imports
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel, MdOutlineWarehouse } from "react-icons/md";
import { FcPlus } from "react-icons/fc";
import { FaWarehouse, FaBoxesStacked } from "react-icons/fa6";

function WareHouseSettings(props) {
  const { t } = useTranslation("settings");
  const currentUser = useSelector((state) => state.user);
  const effectiveOwnerId = getEffectiveOwnerId(currentUser);
  const isDefaultWarehouse = (row) =>
    row?.warehouse?.current_user_id != null &&
    String(row?.warehouse?.current_user_id) === String(effectiveOwnerId);
  const [loading, setLoading] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [wareHouseList, setWareHouseList] = useState([]);
  const [wareHouseData, setWareHouseData] = useState([]);
  const [deleteLoader, setdeleteLoader] = useState(false);
  const [refresh, setRefresh] = useState();

  const [addWarehouseUserModal, setAddWarehouseUserModal] = useState(false);

  const setSnack = useSnackBar();

  const columns = [
    {
      id: 1,
      label: "#",
      render: (row) => <>{row?.warehouse?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 2,
      label: t("fields.name"),
      render: (row) => <>{row?.warehouse?.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("fields.code"),
      render: (row) => <>{row?.warehouse?.code ?? "-"}</>,
      width: "200px",
    },
    {
      id: 4,
      label: t("fields.address"),
      render: (row) => <>{row?.warehouse?.address ?? "-"}</>,
      width: "200px",
    },
    {
      id: 5,
      label: t("fields.type"),
      render: (row) => {
        if (isDefaultWarehouse(row)) {
          return (
            <Tooltip title={t("warehouses.defaultTooltip")}>
              <span className="inline-flex items-center gap-1.5">
                <FaWarehouse />
                {t("warehouses.internal")}
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={t("warehouses.joinedTooltip")}>
            <span className="inline-flex items-center gap-1.5">
              <FaBoxesStacked />
              {t("warehouses.type3pl")}
            </span>
          </Tooltip>
        );
      },
      width: "200px",
    },
    {
      id: 6,
      label: t("fields.status"),
      render: (row) => (
        <StatusIcons
          status={
            row?.is_approved === "false"
              ? "Pending"
              : row?.is_approved === "notapplied"
                ? "Not Applied"
                : row?.is_approved === "true"
                  ? "approved"
                  : "-"
          }
        />
      ),
      width: "200px",
    },
    {
      id: 7,
      label: t("fields.action"),
      render: (row) => (
        <>
          <div className="table_action__wrap">
            {!(row?.is_approved === "false" || row?.is_approved === "true") && (
              <Tooltip title={t("warehouses.manageRequest")}>
                <div
                  className="table_action__btn"
                  onClick={() => sendRequest(row)}
                >
                  {loading[row?.warehouse?.id] ? (
                    <Loader />
                  ) : (
                    <>
                      <MdOutlineWarehouse size={10} />
                      {t("warehouses.requestAccess")}
                    </>
                  )}
                </div>
              </Tooltip>
            )}

            <Tooltip title={t("warehouses.deleteRequest")}>
              <div
                className="table_action__btn"
                onClick={() => setConfirmDelete(row)}
              >
                <MdOutlineCancel size={10} />
                {t("common:actions.delete")}
              </div>
            </Tooltip>
          </div>
        </>
      ),
      width: "200px",
    },
  ];
  const sendRequest = async (data) => {
    setLoading((prevLoading) => ({
      ...prevLoading,
      [data?.warehouse?.id]: true,
    }));

    try {
      const saved = await sendWareHouseRequest(data?.warehouse?.id);
      setLoading((prevLoading) => ({
        ...prevLoading,
        [data?.warehouse?.id]: false,
      }));
      if (!saved.error) {
        setWareHouseData((prevData) => {
          const updatedData = prevData?.data?.map((item) => {
            if (item?.warehouse?.id === data?.warehouse.id) {
              return {
                warehouse: saved?.data?.result,
                is_approved: "false",
              };
            }
            return item;
          });
          return { ...prevData, data: updatedData };
        });

        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("warehouses.requestSent"),
            severity: "success",
          };
        });
      } else {
        setLoading((prevLoading) => ({
          ...prevLoading,
          [data?.warehouse?.id]: false,
        }));
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text:
              saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
            severity: "error",
          };
        });
      }
    } catch (error) {
      setLoading((prevLoading) => ({
        ...prevLoading,
        [data?.warehouse?.id]: false,
      }));
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };
  const selectWareHouse = (value) => {
    if (value === null) {
      return;
    }
    let isFound = wareHouseData?.data?.some(
      (w) => w.warehouse.code === value?.code,
    );
    if (isFound) {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("warehouses.alreadyAdded"),
          severity: "error",
        };
      });
      return;
    } else {
      let array = [
        { warehouse: value, is_approved: "notapplied" },
        ...wareHouseData?.data,
      ];
      setWareHouseData({
        ...wareHouseData,
        data: array,
      });
    }
  };
  const fetchData = async (page, perPage, filter) => {
    const response = await getUserWarehouses(page, perPage, filter);
    if (!response.error) {
      setWareHouseData(response.data.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const handleDeleteRequest = async (data) => {
    setdeleteLoader(true);

    let array = wareHouseData?.data?.filter(
      (d) => d?.warehouse?.id != data?.warehouse?.id,
    );
    setWareHouseData({
      ...wareHouseData,
      data: array,
    });

    if (data.id) {
      const response = await deleteWareHouseRequest(data.id);
      setConfirmDelete(false);
      setdeleteLoader(false);
      if (!response.error && response?.data?.success) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("warehouses.requestDeleted"),
            severity: "success",
          };
        });
      } else {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("warehouses.cancelFailed"),
            severity: "error",
          };
        });
      }
    } else {
      setConfirmDelete(false);
      setdeleteLoader(false);
    }
  };
  const fetchAllWarehouses = async () => {
    try {
      let response1 = await getWareHouseList();
      setWareHouseList(response1.data.result);
    } catch (error) {
      console.log("error", error);
    }
  };
  useEffect(() => {
    fetchAllWarehouses();
  }, []);

  return (
    <>
      <div className="parent_wraper">
        {/* sidebar code  */}
        <SideBar active="2_0" />
        <div className="dash_table_wrapper">
          {/* right side secions  */}
          <Confirm
            open={confirmDelete}
            setOpen={setConfirmDelete}
            handleConfirm={() => handleDeleteRequest(confirmDelete)}
            text={t("warehouses.deleteConfirm")}
            loader={deleteLoader}
          />
          <AddWarehouseUser
            open={addWarehouseUserModal}
            setOpen={setAddWarehouseUserModal}
            setSnack={setSnack}
            setRefresh={setRefresh}
          />

          <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
            <WarehouseTable
              tableTitle={t("warehouses.tableTitle")}
              tableId="warehouseSettingsTableforShipper"
              message={t("warehouses.empty")}
              searchPlaceHolder={t("warehouses.search")}
              columns={columns}
              data={wareHouseData}
              getAllData={fetchData}
              refresh={refresh}
              headerButtons={
                <div className="flex gap-1.5 flex-wrap items-center mr-2">
                  <BoltButton
                    icon={FcPlus}
                    label={t("warehouses.add")}
                    onClick={() => setAddWarehouseUserModal(true)}
                    variant="success"
                    size="sm"
                  />
                  <Autocomplete
                    size="small"
                    id="asynchronous-demo"
                    sx={{ width: 230 }}
                    getOptionLabel={(option) => option.name}
                    options={wareHouseList}
                    onChange={(e, value) => selectWareHouse(value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder={t("warehouses.select")} />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                  />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default WareHouseSettings;
